/**
 * Fechas y zona horaria del negocio.
 *
 * El backend guarda TODO en UTC y agrupa por día/hora de calendario en la zona del negocio
 * (`REPORTS_TIMEZONE`, ver `backend/src/modules/reports/reports.time.ts`). Los reportes reciben
 * `desde`/`hasta` como instantes ISO, así que la pantalla tiene que convertir «el 10 de septiembre»
 * (lo que el admin elige en un `<input type="date">`) al instante UTC en que ese día EMPIEZA y
 * TERMINA en esa zona: si no, un filtro de 7 días se come las primeras 5 horas del día 7.
 *
 * Dos cosas que NO se hacen aquí, a propósito:
 *  - `new Date('2026-09-10').toISOString()`: el motor interpreta la fecha pelada como UTC, así que
 *    el resultado no depende del admin pero sí significa otra cosa que la que él quiso decir;
 *  - un offset fijo (`-05:00`): Colombia no cambia de horario, pero otros negocios sí, y el offset
 *    se calcula con `Intl`, que ya sabe de horarios de verano.
 *
 * `America/Bogota` es el default documentado del backend (`docs/REPORTES.md` §7): se usa mientras la
 * primera respuesta no haya dicho cuál es la zona real (que siempre viene en `periodo.zonaHoraria`).
 */

/** Zona horaria por defecto (espejo de `REPORTS_TIMEZONE` en `backend/.env.example`). */
export const ZONA_HORARIA_POR_DEFECTO = 'America/Bogota';

/**
 * ¿Es una zona horaria IANA válida para `Intl`? (`America/Bogota`, `America/Mexico_City`…)
 *
 * Espejo de `backend/src/lib/timezone.ts`: Ajustes la valida antes de mandarla y el backend la vuelve
 * a validar. Importa porque la zona viaja al prompt (la hora que ve el modelo) y un valor con typo
 * (`America/Bogata`) hacía saltar `RangeError: Invalid time zone specified` en cada respuesta.
 */
export function esZonaHorariaValida(valor: string): boolean {
  try {
    new Intl.DateTimeFormat('es-CO', { timeZone: valor });
    return true;
  } catch {
    return false;
  }
}

/** Partes de una fecha `YYYY-MM-DD` en el calendario (sin zona horaria). */
interface PartesFecha {
  year: number;
  month: number;
  day: number;
}

/** `'2026-09-10'` → `{ year, month, day }`; `null` si no tiene ese formato exacto. */
export function parseFechaISO(fecha: string): PartesFecha | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha.trim());
  if (!partes) return null;

  return { year: Number(partes[1]), month: Number(partes[2]), day: Number(partes[3]) };
}

/** `{ year, month, day }` → `'2026-09-10'` (mes y día con dos dígitos). */
function aFechaISO(partes: PartesFecha): string {
  const mes = String(partes.month).padStart(2, '0');
  const dia = String(partes.day).padStart(2, '0');
  return `${partes.year}-${mes}-${dia}`;
}

/** Un formateador por zona: crear `Intl.DateTimeFormat` es caro y los filtros lo llaman por fecha. */
const formateadores = new Map<string, Intl.DateTimeFormat>();

function formateadorDeZona(zonaHoraria: string): Intl.DateTimeFormat {
  const cacheado = formateadores.get(zonaHoraria);
  if (cacheado) return cacheado;

  const formateador = new Intl.DateTimeFormat('en-US', {
    timeZone: zonaHoraria,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  formateadores.set(zonaHoraria, formateador);
  return formateador;
}

/** Partes de reloj de un instante EN la zona del negocio. */
function partesEnZona(instante: Date, zonaHoraria: string): Record<string, number> {
  const partes: Record<string, number> = {};

  for (const parte of formateadorDeZona(zonaHoraria).formatToParts(instante)) {
    if (parte.type === 'literal') continue;
    partes[parte.type] = Number(parte.value);
  }

  // Algunos motores devuelven la medianoche como 24:00 con `hour12: false`.
  return { ...partes, hour: (partes.hour ?? 0) % 24 };
}

/** Desplazamiento de la zona en ese instante: `horaLocal − horaUTC`, en milisegundos. */
function offsetDeZona(instante: Date, zonaHoraria: string): number {
  const partes = partesEnZona(instante, zonaHoraria);
  const comoSiFueraUtc = Date.UTC(
    partes.year ?? 1970,
    (partes.month ?? 1) - 1,
    partes.day ?? 1,
    partes.hour ?? 0,
    partes.minute ?? 0,
    partes.second ?? 0,
  );

  // Se descartan los milisegundos: `Intl` formatea a segundos, y compararlos ensuciaría el offset.
  return comoSiFueraUtc - (instante.getTime() - instante.getMilliseconds());
}

/**
 * Instante UTC en que `hora:minuto:segundo.milisegundo` de la fecha `YYYY-MM-DD` ocurre en la zona
 * del negocio. `null` si la fecha no tiene el formato esperado.
 *
 * El offset se aplica dos veces porque el propio offset depende del instante (en el cambio de
 * horario, el primero puede caer del otro lado del salto); la segunda pasada lo estabiliza.
 */
export function instanteEnZona(
  fecha: string,
  zonaHoraria: string,
  partes: { hora?: number; minuto?: number; segundo?: number; milisegundo?: number } = {},
): Date | null {
  const parsed = parseFechaISO(fecha);
  if (!parsed) return null;

  const objetivo = Date.UTC(
    parsed.year,
    parsed.month - 1,
    parsed.day,
    partes.hora ?? 0,
    partes.minuto ?? 0,
    partes.segundo ?? 0,
    partes.milisegundo ?? 0,
  );

  let instante = new Date(objetivo);
  for (let vuelta = 0; vuelta < 2; vuelta += 1) {
    instante = new Date(objetivo - offsetDeZona(instante, zonaHoraria));
  }

  return instante;
}

/** Instante UTC del arranque del día (00:00:00.000) en la zona del negocio. */
export function inicioDelDiaEnZona(fecha: string, zonaHoraria: string): Date | null {
  return instanteEnZona(fecha, zonaHoraria);
}

/** Instante UTC del último milisegundo del día (23:59:59.999) en la zona del negocio. */
export function finDelDiaEnZona(fecha: string, zonaHoraria: string): Date | null {
  return instanteEnZona(fecha, zonaHoraria, {
    hora: 23,
    minuto: 59,
    segundo: 59,
    milisegundo: 999,
  });
}

/** Fecha `YYYY-MM-DD` del instante EN la zona del negocio. */
export function fechaEnZona(instante: Date, zonaHoraria: string): string {
  const partes = partesEnZona(instante, zonaHoraria);
  return aFechaISO({
    year: partes.year ?? 1970,
    month: partes.month ?? 1,
    day: partes.day ?? 1,
  });
}

/** «17 sept 2026» del instante EN la zona del negocio (o `'—'` si no es una fecha válida). */
export function formatearEnZona(
  instante: string | Date | null | undefined,
  zonaHoraria: string,
  opciones: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' },
): string {
  if (!instante) return '—';

  const fecha = typeof instante === 'string' ? new Date(instante) : instante;
  if (Number.isNaN(fecha.getTime())) return '—';

  return new Intl.DateTimeFormat('es-CO', { ...opciones, timeZone: zonaHoraria }).format(fecha);
}

/** Hoy (`YYYY-MM-DD`) en la zona del negocio: el «hasta» de los rangos rápidos. */
export function hoyEnZona(zonaHoraria: string, ahora: Date = new Date()): string {
  return fechaEnZona(ahora, zonaHoraria);
}

/**
 * Suma (o resta, con `dias` negativo) días a una fecha `YYYY-MM-DD`.
 *
 * Es aritmética de calendario pura, sobre UTC: no hace falta la zona porque se está moviendo una
 * etiqueta de día (`'2026-03-01'` − 1 día = `'2026-02-28'`), no un instante.
 */
export function sumarDias(fecha: string, dias: number): string {
  const partes = parseFechaISO(fecha);
  if (!partes) return fecha;

  const movida = new Date(Date.UTC(partes.year, partes.month - 1, partes.day + dias));
  return aFechaISO({
    year: movida.getUTCFullYear(),
    month: movida.getUTCMonth() + 1,
    day: movida.getUTCDate(),
  });
}

/** Primer día del mes de una fecha `YYYY-MM-DD` (para el rango «este mes»). */
export function primerDiaDelMes(fecha: string): string {
  const partes = parseFechaISO(fecha);
  if (!partes) return fecha;

  return aFechaISO({ ...partes, day: 1 });
}

/** Cuántos días hay entre dos fechas `YYYY-MM-DD` (positivo si `hasta` es posterior). */
export function diasEntre(desde: string, hasta: string): number | null {
  const inicio = parseFechaISO(desde);
  const fin = parseFechaISO(hasta);
  if (!inicio || !fin) return null;

  const ms =
    Date.UTC(fin.year, fin.month - 1, fin.day) -
    Date.UTC(inicio.year, inicio.month - 1, inicio.day);
  return Math.round(ms / 86400000);
}
